import { faRotate } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getExecutionProgressTrackings,
  patchExecutionProgressTracking
} from 'api/executionProgressTrackings';
import { Flex, Line } from 'components';
import { Disabled } from 'components/Disabled/Disabled';
import { InfoTooltip } from 'components/InfoTooltip/InfoTooltip';
import { ExecutionProgressTracking } from 'components/ExecutionProgressTracking/ExecutionProgressTracking';
import { P } from 'components/Typography/Typography';
import { Button, FieldLabel, Radio, SwitchButton } from 'components/_form';
import { ColorPicker } from 'components/_form/ColorPicker/ColorPicker';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { IExecutionProgressTracking } from 'types/executionProgressTrackings';

export const TasksSettingsForm = () => {
  const [executionProgressTrackings, setExecutionProgressTrackings] = useState<
    IExecutionProgressTracking[]
  >([]);

  const handleGetExecutionProgressTrackings = async () => {
    const executionProgressTrackingsRes = await getExecutionProgressTrackings();

    if ('error' in executionProgressTrackingsRes) {
      return;
    }

    if (executionProgressTrackingsRes.data) {
      setExecutionProgressTrackings(executionProgressTrackingsRes.data);
    }
  };

  const handleSetDefaultExecutionProgressTracking = async (id: number) => {
    const payload = {
      execution_progress_tracking: {
        default: true
      }
    };

    const patchExecutionProgressTrackingRes = await patchExecutionProgressTracking(
      id,
      payload
    );

    if (patchExecutionProgressTrackingRes.data) {
      setExecutionProgressTrackings((prevExecutionProgressTrackings) =>
        prevExecutionProgressTrackings.map((prevExecutionProgressTracking) => ({
          ...prevExecutionProgressTracking,
          default:
            prevExecutionProgressTracking.id === patchExecutionProgressTrackingRes.data.id
        }))
      );
    }
  };

  const handleSetAvailableKindsToBeFromPast = async (
    executionProgressTracking: IExecutionProgressTracking,
    AvailableKindToChange: string
  ) => {
    const { available_kinds_to_be_from_past } = executionProgressTracking;

    const payload = {
      execution_progress_tracking: {
        available_kinds_to_be_from_past:
          available_kinds_to_be_from_past.includes(AvailableKindToChange)
            ? available_kinds_to_be_from_past.filter(
                (availableKindToBeFromPast) =>
                  availableKindToBeFromPast !== AvailableKindToChange
              )
            : [...available_kinds_to_be_from_past, AvailableKindToChange]
      }
    };

    const patchExecutionProgressTrackingRes = await patchExecutionProgressTracking(
      executionProgressTracking.id,
      payload
    );

    if (patchExecutionProgressTrackingRes.data) {
      setExecutionProgressTrackings((prevExecutionProgressTrackings) =>
        prevExecutionProgressTrackings.map((prevExecutionProgressTracking) =>
          prevExecutionProgressTracking.id === patchExecutionProgressTrackingRes.data.id
            ? patchExecutionProgressTrackingRes.data
            : prevExecutionProgressTracking
        )
      );
    }
  };

  useEffect(() => {
    handleGetExecutionProgressTrackings();
  }, []);

  return (
    <Flex width="100%" flexDirection="column" gap="16px" px={4} py={3}>
      <Flex
        alignItems={['stretch', 'flex-start']}
        flexDirection={['column', 'row']}
        gap={['12px', '24px']}
      >
        <Flex width={['100%', '50%']} pr={[0, 2]}>
          <FieldLabel>
            {t('administrationTasksView.tasksExecutionProgressTrackingFollowedBySteps')}
          </FieldLabel>
        </Flex>

        <Flex
          width={['100%', '50%']}
          pl={[0, 2]}
          gap="24px"
          alignItems="center"
          flexWrap="wrap"
        >
          {executionProgressTrackings.map((executionProgressTracking) => (
            <Flex
              key={`TasksSettingsForm-executionProgressTrackings-${executionProgressTracking.id}`}
              alignItems="center"
              gap="8px"
            >
              <Radio
                checked={!!executionProgressTracking.default}
                onClick={() =>
                  handleSetDefaultExecutionProgressTracking(executionProgressTracking.id)
                }
              />
              <ExecutionProgressTracking
                activeAll
                executionProgressTracking={executionProgressTracking}
              />
            </Flex>
          ))}

          <InfoTooltip
            tooltipId="TasksSettingsForm-executionProgressTrackings-tooltip"
            tooltipValue={t(
              'administrationTasksView.executionProgressTrackingsToolTip'
            )}
            tooltipWidth="300px"
          />
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <Flex
        alignItems={['stretch', 'flex-start']}
        flexDirection={['column', 'row']}
        gap={['12px', '24px']}
      >
        <Flex width={['100%', '50%']} pr={[0, 2]}>
          <FieldLabel>
            {t(
              'administrationTasksView.Dates given when confirming PDCA steps can be simple'
            )}
          </FieldLabel>
        </Flex>

        <Flex width={['100%', '50%']} pl={[0, 2]} flexDirection="column" gap="10px">
          {executionProgressTrackings.map((executionProgressTracking) => (
            <Flex
              key={`TasksSettingsForm-executionProgressTrackings-past-dates-${executionProgressTracking.id}`}
              alignItems="center"
              gap="16px"
              flexWrap="wrap"
            >
              {executionProgressTracking.kind.split('').map((kindLetter) => (
                <Flex
                  key={`TasksSettingsForm-executionProgressTrackings-switch-${executionProgressTracking.id}-${kindLetter}`}
                  alignItems="center"
                  gap="8px"
                >
                  <FieldLabel width="fit-content">{kindLetter}</FieldLabel>
                  <SwitchButton
                    withoutMargin
                    value={executionProgressTracking.available_kinds_to_be_from_past.includes(
                      kindLetter
                    )}
                    onChange={() => {
                      handleSetAvailableKindsToBeFromPast(
                        executionProgressTracking,
                        kindLetter
                      );
                    }}
                  />
                </Flex>
              ))}
            </Flex>
          ))}
        </Flex>
      </Flex>

      <Line variant="secondary" />

      <Disabled>
        <Flex
          alignItems={['stretch', 'flex-start']}
          flexDirection={['column', 'row']}
          gap={['12px', '24px']}
        >
          <Flex width={['100%', '50%']} pr={[0, 2]}>
            <P variant="body">
              {t(
                'administrationTasksView.When creating a task, also notify the creator via'
              )}
              :
            </P>
          </Flex>

          <Flex width={['100%', '50%']} pl={[0, 2]} alignItems="center" gap="24px" flexWrap="wrap">
            <Flex alignItems="center" gap="8px">
              <FieldLabel width="fit-content">system</FieldLabel>
              <SwitchButton withoutMargin />
            </Flex>
            <Flex alignItems="center" gap="8px">
              <FieldLabel width="fit-content">e-mail</FieldLabel>
              <SwitchButton withoutMargin />
            </Flex>
          </Flex>
        </Flex>

        <Flex
          my={3}
          alignItems={['stretch', 'flex-start']}
          flexDirection={['column', 'row']}
          gap={['12px', '24px']}
        >
          <Flex width={['100%', '50%']} pr={[0, 2]}>
            <P variant="body" color="red" fontWeight={700}>
              {t('administrationTasksView.Highlighting tasks in the list')}
            </P>
          </Flex>
          <Flex width={['100%', '50%']} pl={[0, 2]} flexDirection="column" gap="4px">
            <P variant="body" color="red" fontWeight={700}>
              {t(
                'administrationTasksView.- color (default Yellow) - time to end'
              )}
            </P>
            <P variant="body" color="red" fontWeight={700}>
              {t('administrationTasksView.- color (default red) - time to end')}
            </P>
            <P variant="body" color="red" fontWeight={700}>
              {t(
                'administrationTasksView.- color (default burgundy) - time to end'
              )}
            </P>
          </Flex>
        </Flex>

        <Flex flexDirection="column" gap="16px">
          <Flex
            alignItems={['stretch', 'center']}
            flexDirection={['column', 'row']}
            gap={['12px', '24px']}
          >
            <Flex width={['100%', '50%']} pr={[0, 2]}>
              <P variant="body">
                {t(
                  'administrationTasksView.Highlight color in the archived task table'
                )}
              </P>
            </Flex>

            <Flex width={['100%', '50%']} pl={[0, 2]} gap="16px" alignItems="center">
              <ColorPicker />
              <Button variant="eucalyptus">
                <Flex alignItems="center" gap="4px">
                  <FontAwesomeIcon icon={faRotate} />
                  {t('profile.default')}
                </Flex>
              </Button>
            </Flex>
          </Flex>

          <Flex
            alignItems={['stretch', 'center']}
            flexDirection={['column', 'row']}
            gap={['12px', '24px']}
          >
            <Flex width={['100%', '50%']} pr={[0, 2]}>
              <P variant="body">
                {t(
                  'administrationTasksView.The highlight color in the canceled job table'
                )}
              </P>
            </Flex>

            <Flex width={['100%', '50%']} pl={[0, 2]} gap="16px" alignItems="center">
              <ColorPicker />
              <Button variant="eucalyptus">
                <Flex alignItems="center" gap="4px">
                  <FontAwesomeIcon icon={faRotate} />
                  {t('profile.default')}
                </Flex>
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Disabled>
    </Flex>
  );
};
